/**
 * MyPlanContent - Component for managing subscription plan
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Bell, CheckCircle, XCircle, QrCode, ExternalLink } from 'lucide-react';
import PixPaymentModal from '../PixPaymentModal';

import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const MyPlanContent = ({ restaurant, onRefresh }) => {
  const [daysLeft, setDaysLeft] = useState(0);
  const [expiryDate, setExpiryDate] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [planStatus, setPlanStatus] = useState('active');
  const [showPixModal, setShowPixModal] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);

  useEffect(() => {
    if (restaurant?.expiry_date) {
      const expiry = new Date(restaurant.expiry_date);
      const now = new Date();
      const diffTime = expiry - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      setExpiryDate(expiry);
      setDaysLeft(diffDays);
      setIsExpired(diffDays <= 0);
      
      if (diffDays <= 0) {
        setPlanStatus('expired');
      } else if (diffDays <= 3) {
        setPlanStatus('expiring');
      } else {
        setPlanStatus('active');
      }
    }
    
    loadPlanInfo();
  }, [restaurant]);

  const loadPlanInfo = async () => {
    try {
      setLoadingPlan(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/restaurant/plan-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanInfo(response.data);
    } catch (error) {
      console.error('Error loading plan info:', error);
    } finally {
      setLoadingPlan(false);
    }
  };

  const handleRenewPix = () => {
    setShowPixModal(true);
  };

  const handlePaymentSuccess = () => {
    console.log('🎉 [PLANO] Pagamento aprovado! Recarregando dados do plano...');
    // Aguardar um pouco para garantir que o backend processou a renovação
    setTimeout(() => {
      loadPlanInfo();
      if (onRefresh) {
        onRefresh();
      }
      // Forçar reload da página para garantir atualização completa
      window.location.reload();
    }, 1000);
  };

  const formatDate = (date) => {
    return date ? new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(date) : '-';
  };

  const getStatusConfig = () => {
    switch (planStatus) {
      case 'expired':
        return {
          gradient: 'from-red-500 to-orange-500',
          bgGradient: 'from-red-500/10 to-orange-500/10',
          borderColor: 'border-red-500/30',
          icon: '⏰',
          title: 'Plano Expirado',
          message: 'Seu período de uso expirou',
          textColor: 'text-red-400',
          pulseColor: 'bg-red-500'
        };
      case 'expiring':
        return {
          gradient: 'from-yellow-500 to-orange-500',
          bgGradient: 'from-yellow-500/10 to-orange-500/10',
          borderColor: 'border-yellow-500/30',
          icon: '⚠️',
          title: 'Plano Expirando em Breve',
          message: 'Renove agora para não perder acesso',
          textColor: 'text-yellow-400',
          pulseColor: 'bg-yellow-500'
        };
      default:
        return {
          gradient: 'from-green-500 to-emerald-500',
          bgGradient: 'from-green-500/10 to-emerald-500/10',
          borderColor: 'border-green-500/30',
          icon: '✅',
          title: 'Plano Ativo',
          message: 'Tudo funcionando perfeitamente',
          textColor: 'text-green-400',
          pulseColor: 'bg-green-500'
        };
    }
  };

  const statusConfig = getStatusConfig();
  const whatsappMessage = encodeURIComponent(
    `Olá! Preciso renovar o plano da minha loja: ${restaurant?.name || 'Minha Loja'}`
  );
  const whatsappUrl = `https://wa.me/5538991504854?text=${whatsappMessage}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Meu Plano</h1>
          <p className="text-gray-400 mt-1">Gerencie seu plano e renovações</p>
        </div>
      </div>

      {/* Card Principal do Plano */}
      <div className={`glass-card overflow-hidden relative border-2 ${statusConfig.borderColor}`}>
        <div className={`absolute inset-0 bg-gradient-to-br ${statusConfig.bgGradient} opacity-50`}></div>
        
        <div className="relative z-10 p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${statusConfig.gradient} flex items-center justify-center text-3xl shadow-lg`}>
                {statusConfig.icon}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{statusConfig.title}</h2>
                <p className={`text-sm ${statusConfig.textColor} font-medium`}>
                  {statusConfig.message}
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className={`w-4 h-4 ${statusConfig.pulseColor} rounded-full`}></div>
              <div className={`absolute inset-0 ${statusConfig.pulseColor} rounded-full animate-ping opacity-75`}></div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm font-medium">Dias Restantes</span>
                <Clock className="w-5 h-5 text-gray-400" />
              </div>
              <div className="flex items-baseline">
                <span className={`text-4xl font-bold ${statusConfig.textColor}`}>
                  {isExpired ? '0' : daysLeft}
                </span>
                <span className="text-gray-400 text-lg ml-2">dias</span>
              </div>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm font-medium">Expira em</span>
                <Bell className="w-5 h-5 text-gray-400" />
              </div>
              <div className="text-white text-lg font-semibold">
                {formatDate(expiryDate)}
              </div>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm font-medium">Status</span>
                <CheckCircle className={`w-5 h-5 ${statusConfig.textColor}`} />
              </div>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${statusConfig.textColor} bg-gradient-to-r ${statusConfig.bgGradient} border ${statusConfig.borderColor}`}>
                {isExpired ? 'Expirado' : daysLeft <= 3 ? 'Crítico' : 'Ativo'}
              </div>
            </div>
          </div>

          {/* Botões de Renovação */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={handleRenewPix}
              className="group relative flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-5 px-8 rounded-xl transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.02] overflow-hidden"
              data-testid="renew-pix-button"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-400 opacity-0 group-hover:opacity-20 transition-opacity"></div>
              <QrCode className="w-7 h-7 relative z-10" />
              <div className="text-left relative z-10">
                <div className="text-lg">Renovar com PIX</div>
                <div className="text-sm text-blue-100 font-normal">
                  Instantâneo - R$ {planInfo?.subscription_price?.toFixed(2).replace('.', ',') || '99,00'}/mês
                </div>
              </div>
            </button>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-5 px-8 rounded-xl transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.02] overflow-hidden"
              data-testid="whatsapp-support-button"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 opacity-0 group-hover:opacity-20 transition-opacity"></div>
              <svg className="w-7 h-7 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <div className="text-left relative z-10">
                <div className="text-lg">Falar com Suporte</div>
                <div className="text-sm text-green-100 font-normal">WhatsApp</div>
              </div>
              <ExternalLink className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* Informações Adicionais */}
          <div className="mt-6 p-4 bg-gray-800/30 rounded-lg border border-gray-700">
            <p className="text-gray-400 text-sm text-center">
              <span className="font-semibold text-white">Loja:</span> {restaurant?.name}
              {restaurant?.subdomain && (
                <span className="ml-4">
                  <span className="font-semibold text-white">Subdomínio:</span> {restaurant.subdomain}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Cards de Informação */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass-card">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-white">Com o plano ativo você tem:</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start space-x-3 text-gray-300">
              <span className="text-green-400 mt-1">✓</span>
              <span>Acesso completo ao painel de gestão</span>
            </li>
            <li className="flex items-start space-x-3 text-gray-300">
              <span className="text-green-400 mt-1">✓</span>
              <span>Cardápio digital ilimitado</span>
            </li>
            <li className="flex items-start space-x-3 text-gray-300">
              <span className="text-green-400 mt-1">✓</span>
              <span>Gerenciamento de pedidos em tempo real</span>
            </li>
            <li className="flex items-start space-x-3 text-gray-300">
              <span className="text-green-400 mt-1">✓</span>
              <span>Suporte técnico via WhatsApp</span>
            </li>
          </ul>
        </div>

        <div className="glass-card">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-white">Quando o plano expira:</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start space-x-3 text-gray-300">
              <span className="text-red-400 mt-1">✕</span>
              <span>Cardápio público fica indisponível</span>
            </li>
            <li className="flex items-start space-x-3 text-gray-300">
              <span className="text-red-400 mt-1">✕</span>
              <span>Clientes não podem fazer pedidos</span>
            </li>
            <li className="flex items-start space-x-3 text-gray-300">
              <span className="text-red-400 mt-1">✕</span>
              <span>Edição de produtos bloqueada</span>
            </li>
            <li className="flex items-start space-x-3 text-gray-300">
              <span className="text-yellow-400 mt-1">!</span>
              <span className="text-yellow-300">Seus dados ficam salvos e seguros</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Banner de Renovação */}
      {(planStatus === 'expiring' || planStatus === 'expired') && (
        <div className={`glass-card border-2 ${statusConfig.borderColor} bg-gradient-to-r ${statusConfig.bgGradient}`}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-6">
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 bg-gradient-to-br ${statusConfig.gradient} rounded-full flex items-center justify-center text-2xl animate-bounce`}>
                {statusConfig.icon}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">
                  {planStatus === 'expired' ? 'Ação Necessária!' : 'Não perca acesso!'}
                </h3>
                <p className={`${statusConfig.textColor} text-sm font-medium`}>
                  {planStatus === 'expired' 
                    ? 'Renove agora para reativar sua loja'
                    : `Seu plano expira em ${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'}`
                  }
                </p>
              </div>
            </div>
            <button
              onClick={handleRenewPix}
              className="glass-button-primary whitespace-nowrap"
            >
              Renovar com PIX
            </button>
          </div>
        </div>
      )}

      {/* PIX Payment Modal */}
      <PixPaymentModal
        isOpen={showPixModal}
        onClose={() => setShowPixModal(false)}
        paymentData={{
          restaurant_id: restaurant?.id,
          months: 1,
          amount: planInfo?.subscription_price || 99
        }}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default MyPlanContent;
